import { Layout } from '@atlas/components';
import cx from 'classnames';
import { useEffect } from 'react';
import { Redirect, Route, Router, Switch } from 'wouter';

import BottomMenu from 'components/BottomMenu';
import DebugInfo from 'components/DebugInfo';
import Footer from 'components/Footer';
import Header from 'components/Header';
import LeftSidebar from 'components/LeftSidebar';
import Notifications from 'components/Notifications/Notifications';
import Popups, { useCookiePopup } from 'components/Popups';
import SandwichMenu from 'components/SandwichMenu';
import ScrollManager from 'components/ScrollManager';
import ServiceMaintenance from 'components/ServiceMaintenance';
import TopLevelHooks from 'components/TopLevelHooks';
import { useConfig } from 'config';
import {
  CasinoGamePage,
  CasinoPage,
  HomePage,
  PaymentsPage,
  ProfilePage,
  PromotionsPage,
  ResetPasswordPage,
  ResponsibleGamingPage,
  SignInPage,
  SignUpPage,
  SportsBookPage,
  TransactionsPage,
} from 'pages';
import type { CasinoGamePageParams } from 'pages/CasinoGamePage/CasinoGamePage';
import type { CasinoPageParams } from 'pages/CasinoPage/CasinoPage';
import { CasinoGamesProvider } from 'pages/CasinoPage/casino.context';
import ProvidersPage from 'pages/ProvidersPage/ProvidersPage';
import StaticPage from 'pages/StaticPage';
import { AuthStatus, useClientProfile, useLanguage } from 'store';
import { useUrlLanguageListener } from 'store/slices/language';
import { useGTM } from 'utils/dataLayer';
import { useDatadog } from 'utils/datadog';
import useLayout, { LayoutType } from 'utils/layout';
import {
  casinoPagePathRegexp,
  casinoPathsRegexp,
  gamePagePathRegexp,
  notAuthPathRegexp,
  notGamePagePathRegexp,
  paymentPagePathRegexp,
  useCustomLocation,
} from 'utils/location';
import {
  PartialMaintenance,
  getAppProp,
  getMaintenanceConfig,
} from 'utils/props';
import { TranslationsProvider } from 'utils/translations';
import useTransport from 'utils/transport';
import { useResetPasswordRedirect } from 'utils/useResetPasswordRedirect';

import createRequest from 'utils/request';
import styles from './App.module.scss';
import Chat from './components/Chat';

function App() {
  useUrlLanguageListener();

  const [data, { auth, logout }] = useClientProfile();
  const { status } = data;
  const isSignedIn = status === AuthStatus.signedIn;
  const { showCookiePopup } = useCookiePopup();
  useEffect(() => {
    showCookiePopup();
    const sessionToken = window.localStorage.getItem('token');
    if (sessionToken) {
      auth(sessionToken);
    } else {
      logout();
    }

    const responseInterceptor = (response: Response) => {
      if (response.status === 401) {
        logout();
      }
    };

    createRequest.interceptors.response.add(responseInterceptor);

    return () => {
      createRequest.interceptors.response.delete(responseInterceptor);
    };
  }, [auth, logout, showCookiePopup]);
  const [{ current: currentLanguage, basePath }] = useLanguage();
  const layout = useLayout();
  const isCasinoEnabled = useConfig('config.siteConfig.casino.enabled');
  const isSportsbookEnabled = useConfig('config.siteConfig.sportsbook.enabled');
  const chatConfig = useConfig('config.siteConfig.integrations.chat');
  const casinoMaintenanceConfig = getMaintenanceConfig(
    PartialMaintenance.casino,
  );
  const sportsbookMaintenanceConfig = getMaintenanceConfig(
    PartialMaintenance.sportsbook,
  );

  useTransport(
    currentLanguage.code,
    getAppProp('project_id'),
    import.meta.env.DEV,
  );

  useGTM();

  useDatadog();

  //TODO: Will be removed after changing the URL in the email.
  useResetPasswordRedirect();

  return (
    <Router hook={useCustomLocation} base={basePath}>
      <TopLevelHooks />
      <ScrollManager />
      {chatConfig && <Chat config={chatConfig} />}
      <TranslationsProvider language={currentLanguage.code}>
        <Layout className={styles.layout}>
          <Layout.Header sticky={true} className={styles.header}>
            <Header />
          </Layout.Header>
          {layout !== LayoutType.mobile && (
            <Layout.LeftSidebar className={styles.sidebar}>
              <Route path={notAuthPathRegexp}>
                <LeftSidebar />
              </Route>
            </Layout.LeftSidebar>
          )}
          <Layout.Content>
            <Popups />
            <main className={cx(styles['content-wrapper'], styles[layout])}>
              <Notifications />
              <SandwichMenu />
              <Switch>
                <Route path="/" component={HomePage} />
                <Route path="/signin" component={SignInPage} />
                <Route path="/signup" component={SignUpPage} />
                <Route path="/reset-password" component={ResetPasswordPage} />
                {isSportsbookEnabled && (
                  <Route path="/sportsbook">
                    {() =>
                      sportsbookMaintenanceConfig ? (
                        <ServiceMaintenance
                          config={sportsbookMaintenanceConfig}
                          part={PartialMaintenance.sportsbook}
                        />
                      ) : (
                        <SportsBookPage />
                      )
                    }
                  </Route>
                )}
                {isSignedIn && (
                  <Route path="/account" nest>
                    <Switch>
                      <Route
                        path="/profile/:section?"
                        component={ProfilePage}
                      />
                      <Route
                        path={paymentPagePathRegexp}
                        component={PaymentsPage}
                      />
                      <Route
                        path="/transactions"
                        component={TransactionsPage}
                      />
                      <Route
                        path="/responsible-gaming/:section?"
                        component={ResponsibleGamingPage}
                      />
                      <Route>
                        <Redirect to="/profile" replace />
                      </Route>
                    </Switch>
                  </Route>
                )}
                <Route path="/promotions/:id?">
                  <PromotionsPage />
                </Route>
                {isCasinoEnabled && (
                  <Route path={casinoPathsRegexp}>
                    <CasinoGamesProvider>
                      <Switch>
                        <Route path={casinoPagePathRegexp}>
                          {(params: CasinoPageParams) =>
                            casinoMaintenanceConfig ? (
                              <ServiceMaintenance
                                config={casinoMaintenanceConfig}
                                part={PartialMaintenance.casino}
                              />
                            ) : (
                              <CasinoPage params={params} />
                            )
                          }
                        </Route>
                        <Route path={gamePagePathRegexp}>
                          {(params: CasinoGamePageParams) =>
                            casinoMaintenanceConfig ? (
                              <ServiceMaintenance
                                config={casinoMaintenanceConfig}
                                part={PartialMaintenance.casino}
                              />
                            ) : (
                              <CasinoGamePage params={params} />
                            )
                          }
                        </Route>
                        <Route path="/game-providers" nest>
                          {() =>
                            casinoMaintenanceConfig ? (
                              <ServiceMaintenance
                                config={casinoMaintenanceConfig}
                                part={PartialMaintenance.casino}
                              />
                            ) : (
                              <ProvidersPage />
                            )
                          }
                        </Route>
                      </Switch>
                    </CasinoGamesProvider>
                  </Route>
                )}
                {status !== AuthStatus.resolving && (
                  <Route>
                    <StaticPage />
                  </Route>
                )}
              </Switch>
              <Route path={notAuthPathRegexp}>
                <Route path={notGamePagePathRegexp}>
                  <Footer />
                </Route>
              </Route>
              <DebugInfo />
            </main>
          </Layout.Content>
          {layout === LayoutType.mobile && (
            <Layout.BottomMenu className={styles['bottom-menu']}>
              <Switch>
                <Route path={notAuthPathRegexp}>
                  <Route path={notGamePagePathRegexp}>
                    <BottomMenu />
                  </Route>
                </Route>
              </Switch>
            </Layout.BottomMenu>
          )}
        </Layout>
      </TranslationsProvider>
    </Router>
  );
}

export default App;
